import { EPlankKind, IRailingConfig, IRailingGeometry, IStairConfig, IVec3, IWoodMaterial, WOOD_MATERIALS } from '@stair-craft/common/util';

/** Railing wood volume in m³ before waste, split by the two railing species. */
export interface IRailingVolume {
    /** Handrail runs and top planks, the upper-floor railing's included. */
    readonly handrail: number;
    /** Balusters, posts and bottom planks, the upper-floor railing's included. */
    readonly baluster: number;
}

/** Weight and cost of the railing wood after waste. */
export interface IRailingWoodEstimate {
    readonly weightKg: number;
    readonly costUsd: number;
}

/** The species by its id; an unknown id falls back to the first species, never throws. */
export function woodMaterial(id: string): IWoodMaterial {
    return WOOD_MATERIALS.find((material: IWoodMaterial) => material.id === id) ?? WOOD_MATERIALS[0];
}

function handrailLength(points: IVec3[]): number {
    let length: number = 0;
    for (let i: number = 0; i < points.length - 1; i++) {
        const from: IVec3 = points[i];
        const to: IVec3 = points[i + 1];
        length += Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z);
    }
    return length;
}

/**
 * Wood volume of every railing member across all railing geometries: the handrail runs on one
 * side, the balusters and posts on the other, as each part has a species of its own.
 */
export function railingVolume(railings: IRailingGeometry[], railConfig: IRailingConfig): IRailingVolume {
    let handrail: number = 0;
    let baluster: number = 0;
    for (const railing of railings) {
        for (const upright of railing.balusters) {
            baluster += (railing.balusterWidth / 1000) ** 2 * (upright.height / 1000);
        }
        for (const post of railing.posts) {
            baluster += (railing.postWidth / 1000) ** 2 * (post.height / 1000);
        }
        if (railing.handrail.points.length >= 2) {
            const length: number = handrailLength(railing.handrail.points);
            handrail += (length / 1000) * (railConfig.handrailWidth / 1000) * (railConfig.handrailHeight / 1000);
        }
        // The top plank goes by the handrail species, the bottom one by the balusters' (SC-CALC-092).
        for (const plank of railing.planks ?? []) {
            const volume: number = (handrailLength(plank.points) / 1000) * (plank.width / 1000) * (plank.height / 1000);
            if (plank.kind === EPlankKind.Top) {
                handrail += volume;
            } else {
                baluster += volume;
            }
        }
    }
    return { handrail, baluster };
}

/** The railing's weight and cost: the handrail at the handrail species, balusters and posts at theirs. */
export function railingWoodEstimate(volume: IRailingVolume, config: IStairConfig, wasteFactor: number): IRailingWoodEstimate {
    const handrailMat: IWoodMaterial = woodMaterial(config.handrailMaterialId);
    const balusterMat: IWoodMaterial = woodMaterial(config.balusterMaterialId);
    const handrailM3: number = volume.handrail * wasteFactor;
    const balusterM3: number = volume.baluster * wasteFactor;
    return {
        weightKg: handrailM3 * handrailMat.density + balusterM3 * balusterMat.density,
        costUsd: handrailM3 * handrailMat.pricePerM3 + balusterM3 * balusterMat.pricePerM3,
    };
}
